import React, {
  createContext,
  useState,
  useContext,
  useMemo,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import { ThemeProvider as MUIThemeProvider } from "@material-ui/core/styles";
import { ptBR } from "@material-ui/core/locale";
import { CssBaseline } from "@material-ui/core";

import { buildTheme } from "../../theme";

const ThemeContext = createContext();

/**
 * Provider único de tema do produto.
 *
 * Antes existiam dois ThemeProvider aninhados: o de `App.js` (que definia
 * `primary`) e este. Como este é o mais interno, ele sobrescrevia o outro por
 * completo — a cor primária configurada em App.js e o locale ptBR nunca
 * chegavam a ser aplicados. Ambos foram consolidados aqui.
 */
export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [locale, setLocale] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem("i18nextLng") || "";
    const normalized = stored.replace("-", "").slice(0, 4);
    if (normalized === "ptBR" || stored.startsWith("pt")) {
      setLocale(ptBR);
    }
  }, []);

  const toggleTheme = () => {
    setDarkMode((prevMode) => !prevMode);
  };

  const theme = useMemo(
    () => buildTheme(darkMode ? "dark" : "light", locale),
    [darkMode, locale]
  );

  const contextValue = useMemo(
    () => ({ darkMode, toggleTheme, setDarkMode }),
    [darkMode]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useThemeContext = () => useContext(ThemeContext);
